import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { listLots, quarantineLot, releaseLot } from "@/lib/lots.functions";
import { useScope } from "@/hooks/use-scope";
import { PageHeader } from "@/components/ui/page-header";
import { ExportMenu } from "@/components/ui/export-menu";
import { FilterBar } from "@/components/ui/filter-bar";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/app/inventory/lots")({ component: LotsPage });

const STATUSES = ["all", "active", "quarantine", "recalled", "consumed"] as const;

function LotsPage() {
  const { tenantId, customerId } = useScope();
  const qc = useQueryClient();
  const listFn = useServerFn(listLots);
  const qFn = useServerFn(quarantineLot);
  const rFn = useServerFn(releaseLot);
  const [status, setStatus] = useState<typeof STATUSES[number]>("all");
  const [search, setSearch] = useState("");

  const { data, isLoading } = useQuery({
    queryKey: ["lots", tenantId, customerId, status],
    enabled: !!tenantId,
    queryFn: () => listFn({ data: { tenantId: tenantId!, customerId: customerId ?? null, status } }),
    placeholderData: keepPreviousData,
  });
  const skuMap = useMemo(() => Object.fromEntries((data?.skus ?? []).map((s: any) => [s.id, s])), [data]);
  const rows = useMemo(() => {
    const list = (data?.rows ?? []) as any[];
    const q = search.trim().toLowerCase();
    if (!q) return list;
    return list.filter((l) => {
      const sku = skuMap[l.sku_id];
      return l.lot_code.toLowerCase().includes(q)
        || (sku?.sku_code?.toLowerCase().includes(q) ?? false)
        || (sku?.name?.toLowerCase().includes(q) ?? false);
    });
  }, [data, search, skuMap]);

  const quarantine = useMutation({
    mutationFn: (id: string) => qFn({ data: { tenantId: tenantId!, lotId: id, reason: "qa_inspection" } }),
    onSuccess: () => { toast.success("Lot quarantined"); qc.invalidateQueries({ queryKey: ["lots"] }); },
    onError: (e: any) => toast.error(e.message),
  });
  const release = useMutation({
    mutationFn: (id: string) => rFn({ data: { tenantId: tenantId!, lotId: id } }),
    onSuccess: () => { toast.success("Lot released"); qc.invalidateQueries({ queryKey: ["lots"] }); },
    onError: (e: any) => toast.error(e.message),
  });

  const daysToExpiry = (d: string | null) => d ? Math.ceil((new Date(d).getTime() - Date.now()) / 86400000) : null;

  return (
    <>
      <PageHeader title="Lots" description="Lot-level inventory with mfg/expiry, country of origin, and quarantine status."
        meta={<Link to="/app/inventory/expiring" className="text-xs text-primary underline">Expiring soon →</Link>}
        actions={tenantId ? (
          <ExportMenu
            entity="lots"
            tenantId={tenantId}
            customerId={customerId}
            getRows={() => rows.map((l: any) => {
              const sku = skuMap[l.sku_id];
              return {
                lot_code: l.lot_code,
                sku_code: sku?.sku_code ?? "",
                sku_name: sku?.name ?? "",
                mfg_date: l.mfg_date ?? "",
                expiry_date: l.expiry_date ?? "",
                received_qty: l.received_qty,
                on_hand_qty: l.on_hand_qty,
                status: l.status,
              };
            })}
            columns={["lot_code","sku_code","sku_name","mfg_date","expiry_date","received_qty","on_hand_qty","status"]}
            filters={{ status, search }}
            disabled={rows.length === 0}
          />
        ) : null}
      />
      <div className="px-4 sm:px-6 py-4 space-y-4">
        <FilterBar search={search} onSearchChange={setSearch} searchPlaceholder="Search lot, SKU…">
          <Select value={status} onValueChange={(v) => setStatus(v as any)}>
            <SelectTrigger className="w-44 h-9"><SelectValue /></SelectTrigger>
            <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s === "all" ? "All statuses" : s}</SelectItem>)}</SelectContent>
          </Select>
        </FilterBar>
        <div className="border rounded-md bg-card overflow-x-auto">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Lot</TableHead><TableHead>SKU</TableHead><TableHead>Mfg</TableHead>
              <TableHead>Expiry</TableHead><TableHead className="text-right">Recv</TableHead>
              <TableHead className="text-right">On hand</TableHead><TableHead>Status</TableHead>
              <TableHead className="text-right w-56">Action</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {isLoading && <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted-foreground">Loading…</TableCell></TableRow>}
              {!isLoading && rows.length === 0 && <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted-foreground">No lots match.</TableCell></TableRow>}
              {rows.map((l: any) => {
                const sku = skuMap[l.sku_id];
                const dte = daysToExpiry(l.expiry_date);
                const expiringSoon = dte != null && dte <= 30;
                return (
                  <TableRow key={l.id}>
                    <TableCell className="font-mono text-xs">
                      <Link to="/app/traceability/$ref" params={{ ref: l.lot_code }} className="hover:underline">{l.lot_code}</Link>
                    </TableCell>
                    <TableCell className="text-xs">{sku ? <><div className="font-mono">{sku.sku_code}</div><div className="text-[11px] text-muted-foreground truncate max-w-[200px]">{sku.name}</div></> : "—"}</TableCell>
                    <TableCell className="text-xs">{l.mfg_date ?? "—"}</TableCell>
                    <TableCell className="text-xs">
                      {l.expiry_date ?? "—"}{dte != null && <span className={`ml-2 text-[11px] ${expiringSoon ? "text-destructive" : "text-muted-foreground"}`}>({dte}d)</span>}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{l.received_qty}</TableCell>
                    <TableCell className="text-right tabular-nums">{l.on_hand_qty}</TableCell>
                    <TableCell><Badge variant={l.status === "active" ? "default" : l.status === "recalled" ? "destructive" : "secondary"}>{l.status}</Badge></TableCell>
                    <TableCell className="text-right space-x-2">
                      {l.status === "active" && <Button size="sm" variant="outline" onClick={() => quarantine.mutate(l.id)} disabled={quarantine.isPending}>Quarantine</Button>}
                      {l.status === "quarantine" && <Button size="sm" variant="outline" onClick={() => release.mutate(l.id)} disabled={release.isPending}>Release</Button>}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </div>
    </>
  );
}